import {
  createContext,
  useContext,
  useState,
  MouseEventHandler,
  useCallback,
  useMemo,
  PropsWithChildren,
} from 'react';

const DropDownContext = createContext<DropDownContextProps | undefined>(
  undefined
);

export const useDropDownContext = (): DropDownContextProps => {
  const context = useContext(DropDownContext);

  if (!context) {
    throw new Error('useDropDownContext must be inside a DropDownProvider');
  }

  return context;
};

/**
 * Hook permettant d'ajouter les props au composant permettant d'ouvrir le menu.
 *
 * @example
 *
 *  const Trigger = (props) => {
 *    const dropDownTriggerProps = useDropDownTriggerProps();
 *
 *    return <button {...props} {...dropDownTriggerProps} />;
 *  };
 *
 */
export const useDropDownTriggerProps = (props?: {
  onClick: MouseEventHandler<any>;
}) => {
  const dropDownContext = useDropDownContext();

  const onClick: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      dropDownContext.open(event.currentTarget);

      if (props?.onClick) {
        props.onClick(event);
      }
    },
    [props?.onClick]
  );

  return useMemo(
    () => ({
      'aria-haspopup': true,
      'aria-expanded': dropDownContext.isOpen ? true : undefined,
      onClick,
    }),
    [dropDownContext.isOpen, onClick]
  );
};

type DropDownProviderProps = PropsWithChildren<{
  canCancel?: boolean;
  onClose?: (isCancel: boolean) => void;
}>;

export const DropDownProvider = ({
  canCancel = false,
  children,
  onClose,
}: DropDownProviderProps) => {
  const [state, setState] = useState<{
    triggerRef?: HTMLElement | null;
    isOpen: boolean;
  }>({ isOpen: false });

  const value = useMemo(() => {
    const open = (triggerRef?: HTMLElement | null) => {
      setState({ ...state, triggerRef: triggerRef || undefined, isOpen: true });
    };

    const close = (isCancel = false) => {
      setState({ ...state, isOpen: false });

      if (onClose) {
        onClose(isCancel);
      }
    };

    return { canCancel, open, close, ...state };
  }, [canCancel, state, onClose]);

  return (
    <DropDownContext.Provider value={value}>
      {children}
    </DropDownContext.Provider>
  );
};

export type DropDownContextProps = {
  open: (triggerRef: null | HTMLElement) => void;
  close: (isCancel?: boolean) => void;
  isOpen: boolean;
  canCancel: boolean;
  triggerRef?: HTMLElement | null;
};
